import { makeAutoObservable, runInAction } from "mobx";
import { showToast } from "@/lib/utils/toast";
import { authService } from "../services";
import { authSessionStore } from "./auth-session.store";

class AuthFormsStore {
  loading = false;
  isRegister = false;

  loginForm = {
    serverError: null,
  };

  registerForm = {
    serverError: null,
  };

  forgotStep = "email"; // 'email' | 'otp'
  forgotEmail = "";
  forgotOtp = ["", "", "", "", "", ""];
  forgotRawToken = null;
  forgotOtpToken = null;
  forgotNewPassword = "";
  forgotConfirmPassword = "";
  forgotReset = {
    serverError: null,
    errors: {},
  };

  changePasswordForm = {
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
    serverError: null,
    errors: {},
  };

  constructor() {
    makeAutoObservable(this);
  }

  setLoading = (val) => {
    this.loading = Boolean(val);
  };

  toggleRegister = (next) => {
    this.isRegister = typeof next === "boolean" ? next : !this.isRegister;
  };

  clearLoginError = () => {
    this.loginForm.serverError = null;
  };

  clearRegisterError = () => {
    this.registerForm.serverError = null;
  };

  login = async (payload) => {
    runInAction(() => {
      this.loading = true;
      this.clearLoginError();
    });

    try {
      const loginPromise = authService.login(payload, {
        onValidationError: (errors) => {
          runInAction(() => {
            this.loginForm.serverError = errors;
          });
        },
      });

      showToast.promise(loginPromise, {
        loading: "Logging in...",
        success: "Logged in successfully!",
        error: (err) => err.message,
      });

      const data = await loginPromise;
      const { user, accessToken, refreshTokenExpiresAt } = data || {};
      runInAction(() => {
        authSessionStore.applyAuthSuccess({
          user,
          accessToken,
          refreshTokenExpiresAt,
        });
      });
      return data;
    } catch (error) {
      return null;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  register = async (payload) => {
    runInAction(() => {
      this.loading = true;
      this.clearRegisterError();
    });

    try {
      const registerPromise = authService.register(payload, {
        onValidationError: (errors) => {
          runInAction(() => {
            this.registerForm.serverError = errors;
          });
        },
      });

      showToast.promise(registerPromise, {
        loading: "Registering...",
        success: "Registered successfully!",
        error: (err) => err.message,
      });

      const data = await registerPromise;
      const { user, accessToken, refreshTokenExpiresAt } = data || {};
      runInAction(() => {
        authSessionStore.applyAuthSuccess({
          user,
          accessToken,
          refreshTokenExpiresAt,
        });
      });
      return data;
    } catch (error) {
      return null;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  // Forgot password setters
  setForgotStep = (step) => {
    this.forgotStep = step;
  };

  setForgotEmail = (email) => {
    this.forgotEmail = email;
  };

  setForgotOtpAt = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      this.forgotOtp[index] = value;
    }
  };

  setForgotNewPassword = (password) => {
    this.forgotNewPassword = password;
  };

  setForgotConfirmPassword = (password) => {
    this.forgotConfirmPassword = password;
  };

  setTokenFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (token) {
      this.forgotRawToken = token;
    }
  };

  clearForgotPasswordState = () => {
    this.forgotStep = "email";
    this.forgotEmail = "";
    this.forgotOtp = ["", "", "", "", "", ""];
    this.forgotRawToken = null;
    this.forgotOtpToken = null;
    this.forgotNewPassword = "";
    this.forgotConfirmPassword = "";
    this.forgotReset = { serverError: null, errors: {} };
  };

  getForgotResetError = (field) => {
    return this.forgotReset.errors?.[field];
  };

  // Forgot password actions
  requestPasswordReset = async () => {
    runInAction(() => {
      this.loading = true;
      this.forgotReset.serverError = null;
    });

    try {
      const resetPromise = authService.requestPasswordReset(
        { email: this.forgotEmail },
        {
          onValidationError: (errors) => {
            runInAction(() => {
              this.forgotReset.serverError = errors;
            });
          },
        },
      );

      showToast.promise(resetPromise, {
        loading: "Sending reset code...",
        success: "Reset code sent to your email!",
        error: (err) => err.message,
      });

      const data = await resetPromise;
      runInAction(() => {
        this.forgotStep = "otp";
      });
      return data;
    } catch (error) {
      return null;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  verifyOtp = async () => {
    runInAction(() => {
      this.loading = true;
      this.forgotReset.serverError = null;
    });

    try {
      const otpCode = this.forgotOtp.join("");
      const verifyPromise = authService.verifyOtp(
        { email: this.forgotEmail, otp: otpCode },
        {
          onValidationError: (errors) => {
            runInAction(() => {
              this.forgotReset.serverError = errors;
            });
          },
        },
      );

      showToast.promise(verifyPromise, {
        loading: "Verifying code...",
        success: "Code verified!",
        error: (err) => err.message,
      });

      const data = await verifyPromise;
      runInAction(() => {
        this.forgotOtpToken = data?.otpToken;
      });
      return data;
    } catch (error) {
      return null;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  confirmResetToken = async () => {
    runInAction(() => {
      this.loading = true;
    });

    try {
      const data = await authService.confirmResetToken(
        { token: this.forgotRawToken },
        { throwOnError: false },
      );
      if (data) {
        runInAction(() => {
          this.forgotOtpToken = data?.token || this.forgotRawToken;
        });
      }
      return data;
    } catch (error) {
      return null;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  submitNewPassword = async (values) => {
    runInAction(() => {
      this.loading = true;
      this.forgotReset.serverError = null;
      this.forgotReset.errors = {};
    });

    try {
      const newPassword = values?.password ?? this.forgotNewPassword;
      const confirmPassword =
        values?.confirmPassword ?? this.forgotConfirmPassword;
      const config = this.forgotOtpToken
        ? { headers: { Authorization: `Bearer ${this.forgotOtpToken}` } }
        : {};

      const resetPromise = authService.updatePassword(
        {
          newPassword,
          confirmPassword,
        },
        {
          ...config,
          onValidationError: (errors) => {
            runInAction(() => {
              if (typeof errors === "object" && !Array.isArray(errors)) {
                this.forgotReset.errors = errors;
              } else {
                this.forgotReset.serverError = errors;
              }
            });
          },
        },
      );

      showToast.promise(resetPromise, {
        loading: "Updating password...",
        success: "Password updated successfully!",
        error: (err) => err.message,
      });

      const data = await resetPromise;
      runInAction(() => {
        this.clearForgotPasswordState();
      });
      return data;
    } catch (error) {
      return null;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  // Change password (for logged-in users)
  setChangePasswordField = (field, value) => {
    this.changePasswordForm[field] = value;
  };

  clearChangePasswordForm = () => {
    this.changePasswordForm = {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
      serverError: null,
      errors: {},
    };
  };

  getChangePasswordError = (field) => {
    return this.changePasswordForm.errors?.[field];
  };

  changePassword = async (values) => {
    runInAction(() => {
      this.loading = true;
      this.changePasswordForm.serverError = null;
      this.changePasswordForm.errors = {};
    });

    try {
      const changePromise = authService.updatePassword(
        {
          oldPassword: values.currentPassword,
          newPassword: values.newPassword,
          confirmPassword: values.confirmPassword,
        },
        {
          onValidationError: (errors) => {
            runInAction(() => {
              if (typeof errors === "object" && !Array.isArray(errors)) {
                this.changePasswordForm.errors = errors;
              } else {
                this.changePasswordForm.serverError = errors;
              }
            });
          },
        },
      );

      showToast.promise(changePromise, {
        loading: "Changing password...",
        success: "Password changed successfully!",
        error: (err) => err.message,
      });

      const data = await changePromise;
      runInAction(() => {
        this.clearChangePasswordForm();
      });
      return data;
    } catch (error) {
      return null;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };
}

export const authFormsStore = new AuthFormsStore();
